import {Controller} from '@stubhub/react-store-provider';

import retryAPI from '../../../../helpers/multiple-api-calls/index';
import ListingModel from '../../../../models/listing';
import SearchInventoryV2 from '../../../../models/listing_search_inventory_v2';
import {
  categoriesSelector,
  daysToEventSelector,
  eventCountryCodeSelector,
  firstPerformerSelector,
  groupingsSelector,
  performersSelector,
  eventInfoLoadingSelector,
  inventoryMarketSelector,
  genreSelector,
} from '../../../../store/event/selectors';
import {
  showImportantInfoModalSelector,
  showApplePaySelector,
  isPayPalPayLaterEnabledSelector,
  showByoMapSelector,
} from '../../../../store/gsConfig/FF/selectors';
import {storeIdSelector} from '../../../../store/gsConfig/selectors';
import {forceShowDisclaimerSelector} from '../../../../store/header/selectors';
import {advisoryCurrencySelector} from '../../components/react-tickets-quantity-picker/selectors';
import {closeImportantDisclaimer} from '../../reducers';

import ActionTypes from './action-types';
import {
  cartIdSelector,
  dataSelector,
  exchangeRateSelector,
  inventoryMapSelector,
  listingSelectedSelector,
  listingSelector,
  localeSelector,
  paymentMethodsSelector,
  sectionStatsSelector,
  showFanProtectWarrantySelector,
  showRestrictionsSelector,
} from './selectors';
import {
  getFilteredPaymentMethods,
  getPayLaterAmount,
  isCurrencyEligibleForPayLater,
  isPayLaterAmountVisible,
  needsLocalDeliveryAddress,
} from './utils';

function setBYOPrice({buildyourorder = {}}, data) {
  const {isPriceLoading, price, sectionId} = data;

  return {
    buildyourorder: {...buildyourorder, price, isPriceLoading, listing: {...buildyourorder.listing, price}, sectionId},
  };
}

function destroy() {
  return (dispatch) => {
    dispatch({type: ActionTypes.RESET});
  };
}

const NonSearchInventoryV2Listing = ListingModel({});
const SearchInventoryV2Listing = SearchInventoryV2();

/**
 * Check if we have price, id, and quantity returned from api
 *@param response Object, response data from API
 *@return {boolean} return true only if we have all these returned
 *
 */
function reloadChecker(response = {}) {
  const {id, quantity, pricePerProduct = {}} = response;
  const {amount} = pricePerProduct;

  return !!(id && quantity && amount);
}

function resetBYOSectionId() {
  return (dispatch) => {
    dispatch({
      type: ActionTypes.RESET_SID,
      sectionId: null,
    });
  };
}

/**
 * If the first api call fails, made two additional api calls, stop if either is successful
 *otherwise return error equals true to store
 *Error is passed as props to the view
 *@param listingId Number,listing id
 *@param loading boolean,if or not BYO is loading
 *@param retryTimes Number, number of API retries
 *
 */
function getListingDetail({listingId}, retryTimes = 1, options = {}, loading = true) {
  return async (dispatch, getState) => {
    const state = getState();
    try {
      dispatch({
        type: ActionTypes.BYO_LOADING,
        error: false,
        loading: true,
        listingLoading: !loading,
        isPriceLoading: true,
        listing: {},
        trader: {},
      });
      const {gsConfig = {}, lang} = getState();
      const listingsMap = inventoryMapSelector(getState());
      const headers = _getFetchRequestHeaders(lang);
      const {eventId} = state.event;

      const queryParams = {
        shstore: gsConfig.shstoreId,
        dt: new Date().getTime(),
      };
      const selectedListing = listingSelectedSelector(state) || {};

      const preListing = listingsMap[listingId] || {};

      if (!preListing.price || !preListing.sectionId) {
        const parsedOptions = Object.assign(options, {listingIds: listingId});
        getSingleListingFromMultipleListingAPI(dispatch, eventId, headers, parsedOptions);
      }

      const apiCall = () =>
        NonSearchInventoryV2Listing.get({
          listingId,
          query: queryParams,
          headers,
        }).then((response) => {
          if (!reloadChecker(response)) {
            throw new Error('error');
          } else {
            return response;
          }
        });

      const listing = await retryAPI(retryTimes, apiCall);

      const resultListing = {...preListing, ...selectedListing, ...listing};

      const loadedPayload = {
        type: ActionTypes.BYO_LOADED,
        listing: resultListing,
        loading: false,
        listingLoading: false,
        isPriceLoading: false,
      };
      if (preListing.price) {
        loadedPayload.price = preListing.price;
      }
      if (resultListing.sectionId) {
        loadedPayload.sectionId = resultListing.sectionId;
      }

      dispatch(loadedPayload);
    } catch (e) {
      dispatch({
        type: ActionTypes.BYO_ERROR,
        error: true,
        loading: false,
        listingLoading: false,
        isPriceLoading: false,
      });
    }
  };
}

function _getFetchRequestHeaders(language) {
  const ret = {
    Authorization: process.env.REACT_APP_API_SECRET,
  };
  if (language) {
    ret['Accept-Language'] = language;
  }

  return ret;
}

function getSingleListingFromMultipleListingAPI(dispatch, eventId, headers, options = {}) {
  dispatch({type: ActionTypes.PRICE_LOADING, isPriceLoading: true});

  return SearchInventoryV2Listing.get({
    headers,
    eventId,
    ...options,
  })
    .then(({listing: listings}) => {
      const [listing] = listings;
      dispatch({
        type: ActionTypes.PRICE_LOADED,
        isPriceLoading: false,
        price: listing.price,
        sectionId: listing.sectionId,
      });

      return listing;
    })
    .catch(() => {
      dispatch({type: ActionTypes.PRICE_ERROR, isPriceLoading: false, error: true});
    });
}

function fetchSelectedListing(eventId, listingId, options = {}) {
  return async (dispatch, getState) => {
    const {lang} = getState() || {};
    const headers = _getFetchRequestHeaders(lang);
    let {quantity} = options;
    if (quantity === 5) {
      quantity = '>5';
    }
    const parsedOptions = Object.assign(options, {
      listingIds: listingId,
      quantity,
    });

    await getSingleListingFromMultipleListingAPI(dispatch, eventId, headers, parsedOptions);
  };
}

const controller = new Controller({
  namespace: 'buildyourorder',
  mapStateToProps(state, ownProps) {
    const {priceWithFees, byoQuantity} = ownProps;
    const localState = this.getLocalState(state) || {error: null};
    const {loading, error, listingLoading, seatNumber, isPriceLoading, price} = localState;
    const listing = listingSelector(localState);
    const {ticketMedium, deliveryMethods} = listing;
    const {currencyCode, ...eventData} = dataSelector(state);
    const paymentMethods = paymentMethodsSelector(state);
    const showApplePay = showApplePaySelector(state);
    const isPaypalPayLaterFFEnabled = isPayPalPayLaterEnabledSelector(state);
    const selectedCurrencyCode = advisoryCurrencySelector(state);
    const showPayLaterWithAmount = isPayLaterAmountVisible(
      priceWithFees,
      ticketMedium,
      selectedCurrencyCode,
      currencyCode
    );

    return {
      storeId: storeIdSelector(state),
      loading,
      error,
      listingLoading: listingLoading || false,
      seatNumber,
      ticketMedium,
      showLocalDeliveryNote: needsLocalDeliveryAddress(ticketMedium, deliveryMethods),
      listing,
      listingsMap: inventoryMapSelector(state),
      locale: localeSelector(state),
      ...eventData,
      sectionStats: sectionStatsSelector(state),
      cartId: cartIdSelector(state),
      paymentMethods,
      exchangeRate: exchangeRateSelector(state),
      price,
      daysToEvent: daysToEventSelector(state),
      eventCountry: eventCountryCodeSelector(state),
      categories: categoriesSelector(state),
      groupings: groupingsSelector(state),
      performers: performersSelector(state),
      firstPerformer: firstPerformerSelector(state),
      isEventInfoLoading: eventInfoLoadingSelector(state),
      genre: genreSelector(state),
      inventoryMarket: inventoryMarketSelector(state),
      isPriceLoading,
      showImportantInfoModal: showImportantInfoModalSelector(state) || false,
      forceShowDisclaimer: forceShowDisclaimerSelector(state),
      filteredPaymentMethods: getFilteredPaymentMethods(paymentMethods, showApplePay),
      isPaypalPayLaterEnabled: isPaypalPayLaterFFEnabled && isCurrencyEligibleForPayLater(currencyCode),
      payLaterAmount: getPayLaterAmount(showPayLaterWithAmount, price?.amount ? price.amount * byoQuantity : 0),
      showFanProtectWarranty: showFanProtectWarrantySelector(state),
      showRestrictions: showRestrictionsSelector(state),
      showMap: showByoMapSelector(state),
    };
  },
  actionCreators: {
    getListingDetail,
    fetchSelectedListing,
    destroy,
    resetBYOSectionId,
    forceOpenDisclaimer,
    closeDisclaimer,
  },
  reducers: [
    ActionTypes.BYO_LOADING,
    ActionTypes.BYO_LOADED,
    ActionTypes.BYO_ERROR,
    ActionTypes.PRICE_LOADING,
    {action: ActionTypes.PRICE_LOADED, handler: setBYOPrice},
    ActionTypes.PRICE_ERROR,
    ActionTypes.RESET_SID,
    {action: 'EVENT:DISCLAIMER:CLOSE', handler: closeImportantDisclaimer},
  ],
});

function closeDisclaimer() {
  return (dispatch) => {
    dispatch({
      type: 'EVENT:DISCLAIMER:CLOSE',
      showDisclaimer: false,
    });
  };
}

function forceOpenDisclaimer() {
  return (dispatch) => {
    dispatch({
      type: 'EVENT:DISCLAIMER:OPEN',
      forceShowDisclaimer: true,
    });
  };
}

controller.addResetReducer([ActionTypes.RESET]);

export default controller;
